import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSwitch } from '../components/LanguageSwitch';
import Logo from '../components/Logo';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { api } from '../services/api';

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying'); // verifying, success, error, expired
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const { setToken, refreshUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const hasVerified = useRef(false); // Prevent double execution

  const CANONICAL_ORIGIN = 'https://klagma.kolrus.com';
  const renderMessage = (maybeKeyOrText) => {
    const v = String(maybeKeyOrText || '').trim();
    if (!v) return '';
    // Only run through i18n when it looks like one of our translation keys.
    if (v.startsWith('verifyEmail.') || v.startsWith('common.')) return t(v);
    return v;
  };
  const goToDashboard = () => {
    // If verification happened on the legacy S3 static website host, force navigation to the canonical site.
    if (window.location.origin !== CANONICAL_ORIGIN) {
      window.location.href = `${CANONICAL_ORIGIN}/User_page`;
      return;
    }
    navigate('/User_page');
  };

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('verifyEmail.tokenMissing');
      setLoading(false);
      return;
    }

    // Only verify once - prevent double execution
    if (!hasVerified.current) {
      hasVerified.current = true;
      verifyEmail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- verify once when token is set
  }, [token]);

  const verifyEmail = async () => {
    
    try {
      const response = await api.get(`/auth/verify-email?token=${token}`);
      
      if (response.data.token) {
        // Store the new token if provided
        setToken(response.data.token);
        localStorage.setItem('token', response.data.token);
        
        // Update user object with verified status
        if (response.data.user) {
          const updatedUser = {
            id: response.data.user.id,
            email: response.data.user.email,
            emailVerified: response.data.user.emailVerified || true,
          };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
        
        // Refresh user data to get updated emailVerified status
        if (refreshUser) {
          setTimeout(() => {
            refreshUser();
          }, 100);
        }
      }

      setStatus('success');
      setMessage(response.data.message || 'verifyEmail.success');
      
      // Don't auto-redirect - let user click button to go to dashboard
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Verification failed';
      const errorMessageLc = String(errorMessage || '').toLowerCase();
      
      // Check if email is already verified
      if (
        errorMessageLc.includes('already verified') ||
        errorMessageLc.includes('already been used') ||
        errorMessageLc.includes('already used')
      ) {
        setStatus('success');
        // Prefer server message if present; fall back to translation key.
        setMessage(errorMessage || 'verifyEmail.alreadyVerified');
        if (error.response?.data?.token) {
          setToken(error.response.data.token);
          localStorage.setItem('token', error.response.data.token);
          
          // Update user object
          if (error.response?.data?.user) {
            const updatedUser = {
              id: error.response.data.user.id,
              email: error.response.data.user.email,
              emailVerified: true,
            };
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
          
          if (refreshUser) {
            setTimeout(() => {
              refreshUser();
            }, 100);
          }
        }
        // Don't auto-redirect - let user click button
        return;
      }
      
      setStatus('error');
      setMessage(errorMessage);
      
      if (errorMessageLc.includes('expired') || errorMessageLc.includes('invalid')) {
        setStatus('expired');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setLoading(true);
      // Try to get email from stored user, otherwise use token
      const storedUser = localStorage.getItem('user');
      const userEmail = storedUser ? JSON.parse(storedUser).email : null;
      
      // Pass the verification token and/or email so we can resend without authentication
      const payload = { token: token };
      if (userEmail) {
        payload.email = userEmail;
      }
      
      await api.post('/auth/resend-verification', payload);
      setStatus('success');
      setMessage('verifyEmail.sent');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.error || 'verifyEmail.resendFailed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFFFFF', color: '#1B211A' }}>
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl" style={{ borderBottom: '1px solid rgba(169, 201, 232, 0.3)', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
<div className="max-w-6xl mx-auto px-4 h-12 flex items-center justify-between gap-4">
            <Logo />
          <div className="flex items-center gap-2">
            <LanguageSwitch />
            <Link 
              to="/" 
              className="text-xs md:text-sm transition-colors hover:opacity-80"
              style={{ color: 'rgb(100, 130, 160)' }}
            >
              {t('common.backToHome')}
            </Link>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-10 md:py-16">
        <div className="w-full max-w-md">
          <div className="rounded-2xl p-8 md:p-10" style={{ backgroundColor: 'rgba(169, 201, 232, 0.05)', border: '1px solid rgba(169, 201, 232, 0.2)' }}>
            {loading && status === 'verifying' ? (
              <div className="text-center">
                <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: 'rgb(169, 201, 232)' }} />
                <h2 className="text-xl font-semibold mb-2">{t('verifyEmail.verifying')}</h2>
                <p className="text-sm" style={{ color: 'rgb(100, 130, 160)' }}>{t('verifyEmail.verifyingWait')}</p>
              </div>
            ) : status === 'success' ? (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                  <CheckCircle className="w-10 h-10" style={{ color: '#22c55e' }} />
                </div>
                <h2 className="text-xl font-semibold mb-2">{t('verifyEmail.emailVerified')}</h2>
                <p className="text-sm mb-6" style={{ color: 'rgb(100, 130, 160)' }}>{renderMessage(message)}</p>
                <div className="space-y-3">
                  <button
                    onClick={goToDashboard}
                    className="w-full py-3 px-4 rounded-lg font-medium transition-colors"
                    style={{ backgroundColor: 'rgb(169, 201, 232)', color: '#FFFFFF' }}
                  >
                    {t('verifyEmail.goToDashboard')}
                  </button>
                  <Link
                    to="/login"
                    className="block w-full py-3 px-4 rounded-lg font-medium text-center transition-colors"
                    style={{ backgroundColor: 'rgba(169, 201, 232, 0.1)', color: 'rgb(169, 201, 232)' }}
                  >
                    {t('verifyEmail.goToLogin')}
                  </Link>
                </div>
              </div>
            ) : status === 'expired' ? (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                  <XCircle className="w-10 h-10" style={{ color: '#ef4444' }} />
                </div>
                <h2 className="text-xl font-semibold mb-2">{t('verifyEmail.linkExpired')}</h2>
                <p className="text-sm mb-6" style={{ color: 'rgb(100, 130, 160)' }}>{renderMessage(message)}</p>
                <button
                  onClick={handleResend}
                  disabled={loading}
                  className="w-full py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: 'rgb(169, 201, 232)', color: '#FFFFFF' }}
                >
                  {loading ? t('verifyEmail.sending') : t('verifyEmail.resend')}
                </button>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                  <XCircle className="w-10 h-10" style={{ color: '#ef4444' }} />
                </div>
                <h2 className="text-xl font-semibold mb-2">{t('verifyEmail.failedTitle')}</h2>
                <p className="text-sm mb-6" style={{ color: 'rgb(100, 130, 160)' }}>{renderMessage(message)}</p>
                <div className="space-y-3">
                  <button
                    onClick={handleResend}
                    disabled={loading}
                    className="w-full py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
                    style={{ backgroundColor: 'rgb(169, 201, 232)', color: '#FFFFFF' }}
                  >
                    {loading ? t('verifyEmail.sending') : t('verifyEmail.resend')}
                  </button>
                  <Link
                    to="/login"
                    className="block w-full py-3 px-4 rounded-lg font-medium text-center transition-colors"
                    style={{ backgroundColor: 'rgba(169, 201, 232, 0.1)', color: 'rgb(169, 201, 232)' }}
                  >
                    {t('verifyEmail.goToLogin')}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default VerifyEmail;

